import React, { useState, useEffect, useCallback } from 'react';
import { Eye, Trash2, FileText, Calculator, Search, Filter, RefreshCw } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import EstimateDetailModal from './EstimateDetailModal';
import { formatItemCode } from '../../utils/itemCodeFormatter';
import { convertEstimateToBilling } from '../../utils/estimatePreFill';
import usePermissions from '../../hooks/usePermissions';

const ViewEstimates = () => {
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission('view-estimates', 'delete');
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Session-based authentication

  // Fetch estimates
  const fetchEstimates = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        ...(currencyFilter !== 'all' && { currency: currencyFilter }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(searchTerm && { customer: searchTerm })
      });

      const response = await fetch(`/api/estimates?${queryParams}`, {
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        setEstimates(data.estimates);
        setPagination(data.pagination);
      } else {
        console.error('Failed to fetch estimates:', data.message);
      }
    } catch (error) {
      console.error('Error fetching estimates:', error);
    } finally {
      setLoading(false);
    }
  }, [currencyFilter, statusFilter, searchTerm, pagination.limit]);

  // Delete estimate
  const deleteEstimate = async (id) => {
    if (!canDelete) return;
    if (!window.confirm('Are you sure you want to delete this estimate?')) {
      return;
    }

    try {
      const response = await fetch(`/api/estimates/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        setEstimates(estimates.filter(est => est.id !== id));
        alert('Estimate deleted successfully');
      } else {
        alert('Failed to delete estimate: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting estimate:', error);
      alert('Error deleting estimate');
    }
  };

  // Convert estimate to sale — navigate to the appropriate billing module,
  // pre-filling fields from the estimate (+ any rate overrides from the modal).
  const convertToSale = async (estimate, overrides = {}) => {
    convertEstimateToBilling(estimate, overrides);

    // Close the modal
    setShowDetailModal(false);
    setSelectedEstimate(null);
  };

  // Open detail modal — fetch full record so stone_details and all fields are present
  const openDetailModal = async (estimate) => {
    setSelectedEstimate(estimate);   // show modal immediately with list data
    setShowDetailModal(true);
    setLoadingDetail(true);
    try {
      const res = await fetch(`/api/estimates/${estimate.id}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success && data.estimate) {
        // parse stone_details if it came back as a string
        const full = { ...data.estimate };
        if (typeof full.stone_details === 'string') {
          try { full.stone_details = JSON.parse(full.stone_details); } catch { full.stone_details = []; }
        }
        setSelectedEstimate(full);
      }
    } catch (err) {
      console.error('Failed to fetch full estimate:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchEstimates();
  }, [fetchEstimates]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchTerm !== '' || searchTerm === '') {
        fetchEstimates(1);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, fetchEstimates]);

  const handlePageChange = (newPage) => {
    fetchEstimates(newPage);
  };

  const formatCurrency = (amount, currency) => {
    if (currency === 'USD') {
      return `$${parseFloat(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return '₹' + Math.round(parseFloat(amount || 0)).toLocaleString('en-IN');
  };

  // Pick the best available total to show in the list row
  const getDisplayTotal = (estimate) => {
    if (estimate.currency === 'USD') {
      const usd = parseFloat(estimate.grand_total_usd);
      if (usd > 0) return formatCurrency(usd, 'USD');
    }
    const inr = parseFloat(estimate.grand_total_inr || estimate.grand_total);
    return inr > 0 ? formatCurrency(inr, 'INR') : '—';
  };

  // Cumulative reduction vs. the standard/catalog price this item was first quoted
  // at — screen-only context, never shown on PDFs/email/WhatsApp.
  const getReducedAmount = (estimate) => {
    const catalog = parseFloat(estimate.catalog_price_inr);
    const current = parseFloat(estimate.grand_total_inr || estimate.grand_total);
    if (!(catalog > 0) || !(current > 0)) return null;
    const reduced = catalog - current;
    return reduced > 0 ? reduced : null;
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      active: 'bg-green-100 text-green-800',
      converted: 'bg-blue-100 text-blue-800',
      expired: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <PageIdentifier pageId={SCREEN_IDS?.ESTIMATES?.VIEW_ESTIMATES || 'EST-003'} pageName="View Estimates" />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
          <FileText className="mr-2" />
          Customer Estimates
        </h1>
        <p className="text-gray-600">View and manage all customer estimates</p>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Search className="inline w-4 h-4 mr-1" />
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Customer name, phone, or item code..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calculator className="inline w-4 h-4 mr-1" />
              Currency
            </label>
            <select
              value={currencyFilter}
              onChange={(e) => setCurrencyFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Currencies</option>
              <option value="INR">INR Only</option>
              <option value="USD">USD Only</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Filter className="inline w-4 h-4 mr-1" />
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="converted">Converted</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => fetchEstimates(1)}
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 flex items-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Estimates Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2">Loading estimates...</span>
          </div>
        ) : estimates.length === 0 ? (
          <div className="text-center p-8">
            <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Estimates Found</h3>
            <p className="text-gray-600">No estimates match your current filters.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estimate #
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Currency
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {estimates.map((estimate) => (
                    <tr key={estimate.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {estimate.estimate_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{estimate.customer_name}</div>
                        <div className="text-sm text-gray-500">{estimate.customer_phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatItemCode(estimate.item_code)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${estimate.currency === 'USD' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                          {estimate.currency}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {getDisplayTotal(estimate)}
                        {getReducedAmount(estimate) != null && (
                          <div className="text-xs font-normal text-emerald-600">
                            Reduced ₹{Math.round(getReducedAmount(estimate)).toLocaleString('en-IN')}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {/* estimate_date comes back from the API as either a bare "YYYY-MM-DD"
                            or a full ISO datetime (pg serializes DATE columns as
                            "YYYY-MM-DDT00:00:00.000Z") — take just the date portion.
                            Parsing that directly reads as UTC midnight, which
                            toLocaleDateString() then renders in the browser's local zone,
                            shifting it back a day west of UTC. Appending a local
                            time-of-day avoids that shift. */}
                        {estimate.estimate_date
                          ? new Date(`${String(estimate.estimate_date).slice(0, 10)}T00:00:00`).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(estimate.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openDetailModal(estimate)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {estimate.status === 'active' && (
                            <button
                              onClick={() => openDetailModal(estimate)}
                              className="text-green-600 hover:text-green-900 p-1 rounded"
                              title="Convert to Sale (opens detail view)"
                            >
                              <Calculator className="w-4 h-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => deleteEstimate(estimate.id)}
                              className="text-red-600 hover:text-red-900 p-1 rounded"
                              title="Delete Estimate"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span>
                      {' '}to{' '}
                      <span className="font-medium">
                        {Math.min(pagination.page * pagination.limit, pagination.total)}
                      </span>
                      {' '}of{' '}
                      <span className="font-medium">{pagination.total}</span>
                      {' '}results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      {[...Array(pagination.pages)].map((_, i) => (
                        <button
                          key={i + 1}
                          onClick={() => handlePageChange(i + 1)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pagination.page === i + 1
                            ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.pages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Estimate Detail Modal */}
      <EstimateDetailModal
        estimate={selectedEstimate}
        isOpen={showDetailModal}
        loadingDetail={loadingDetail}
        onConvertToSale={convertToSale}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedEstimate(null);
        }}
      />
    </div>
  );
};

export default ViewEstimates;
